/**
 * CodeEditorIDE.js
 * IDE multi-fichiers basé sur Monaco Editor avec arborescence de fichiers,
 * zones verrouillées et linting Python.
 */
import React from "react";
import Editor from "@monaco-editor/react";
import {
    Box, Chip, IconButton, Menu, MenuItem, Paper, Stack, TextField,
    Tooltip, Typography, ListItemIcon, ListItemText, useTheme,
} from "@mui/material";
import CreateNewFolderRoundedIcon from "@mui/icons-material/CreateNewFolderRounded";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import {lintPython, isPyodideReady, preloadPyodide} from "./PythonLinter";

// ── Helpers zones verrouillées ─────────────────────────────────────────────
function computeLockRanges(content) {
    const ranges = [];
    const lines = content.split("\n");
    let inLock = false, lockStart = -1;
    lines.forEach((line, i) => {
        if (line.includes("LOCK:") && line.includes(":START")) { inLock = true; lockStart = i; }
        if (line.includes("LOCK:") && line.includes(":END")) { if (inLock) ranges.push({start: lockStart, end: i}); inLock = false; }
    });
    return ranges;
}

// ── Construire un arbre depuis les chemins plats ──────────────────────────
function buildTree(filePaths, readOnlyPaths) {
    const root = {name: "", children: {}, files: []};
    const addPath = (path, isReadOnly) => {
        const parts = path.split("/");
        let node = root;
        for (let i = 0; i < parts.length - 1; i++) {
            if (!node.children[parts[i]]) {
                node.children[parts[i]] = {name: parts[i], children: {}, files: []};
            }
            node = node.children[parts[i]];
        }
        node.files.push({name: parts[parts.length - 1], path, readOnly: !!isReadOnly});
    };
    filePaths.forEach(p => addPath(p, false));
    readOnlyPaths.forEach(p => addPath(p, true));
    return root;
}

// ── Icône de fichier selon l'extension ────────────────────────────────────
function fileIcon(name, isReadOnly, isActive) {
    if (isReadOnly) return <LockRoundedIcon sx={{fontSize: 14, color: "text.disabled"}}/>;
    if (name.endsWith(".py")) return <InsertDriveFileRoundedIcon sx={{fontSize: 14, color: "#306998"}}/>;
    if (name.endsWith(".R")) return <InsertDriveFileRoundedIcon sx={{fontSize: 14, color: "#276DC3"}}/>;
    if (name.endsWith(".json")) return <InsertDriveFileRoundedIcon sx={{fontSize: 14, color: "#f5a623"}}/>;
    if (name.endsWith(".yaml") || name.endsWith(".yml")) return <InsertDriveFileRoundedIcon sx={{fontSize: 14, color: "#cb171e"}}/>;
    if (name.endsWith(".txt")) return <InsertDriveFileRoundedIcon sx={{fontSize: 14, color: "text.disabled"}}/>;
    return <InsertDriveFileRoundedIcon sx={{fontSize: 14, color: isActive ? "primary.main" : "text.disabled"}}/>;
}

// ── Noeud d'arbre (récursif) ─────────────────────────────────────────────
function TreeNode({node, depth, activeFile, onSelectFile, onCreateFile, onCreateFolder, onDeleteFile, onRefreshFile, refreshableFiles, protectedFiles}) {
    const [open, setOpen] = React.useState(true);
    const [contextMenu, setContextMenu] = React.useState(null);

    const folderName = node.name;

    const handleContext = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({mouseX: e.clientX, mouseY: e.clientY});
    };

    const sortedFolders = Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name));
    const sortedFiles = [...node.files].sort((a, b) => a.name.localeCompare(b.name));

    // Chemin complet du dossier courant
    const folderPath = depth === 0 ? "" : (node._fullPath || "");

    return (
        <>
            {/* En-tête du dossier (pas affiché pour la racine) */}
            {depth > 0 && (
                <Box
                    onClick={() => setOpen(!open)}
                    onContextMenu={handleContext}
                    sx={{
                        display: "flex", alignItems: "center", gap: 0.25,
                        pl: depth * 1.25, pr: 0.5, py: 0.15,
                        cursor: "pointer", userSelect: "none",
                        borderRadius: 0.5,
                        "&:hover": {bgcolor: "action.hover"},
                    }}
                >
                    {open
                        ? <ExpandMoreIcon sx={{fontSize: 14, color: "text.disabled"}}/>
                        : <ChevronRightIcon sx={{fontSize: 14, color: "text.disabled"}}/>}
                    {open
                        ? <FolderOpenRoundedIcon sx={{fontSize: 14, color: "#e8a838", ml: 0.15}}/>
                        : <FolderRoundedIcon sx={{fontSize: 14, color: "#e8a838", ml: 0.15}}/>}
                    <Typography variant="caption" noWrap sx={{fontSize: 11, color: "text.primary", ml: 0.25, fontWeight: 600}}>
                        {folderName}
                    </Typography>
                </Box>
            )}

            {/* Contenu du dossier */}
            {(depth === 0 || open) && (
                <>
                    {sortedFolders.map(child => (
                        <TreeNode
                            key={child.name}
                            node={{...child, _fullPath: folderPath ? `${folderPath}/${child.name}` : child.name}}
                            depth={depth + 1}
                            activeFile={activeFile}
                            onSelectFile={onSelectFile}
                            onCreateFile={onCreateFile}
                            onCreateFolder={onCreateFolder}
                            onDeleteFile={onDeleteFile}
                            onRefreshFile={onRefreshFile}
                            refreshableFiles={refreshableFiles}
                            protectedFiles={protectedFiles}
                        />
                    ))}
                    {sortedFiles.map(file => (
                        <Box
                            key={file.path}
                            onClick={() => onSelectFile(file.path)}
                            onContextMenu={(e) => {
                                if (!file.readOnly && !protectedFiles.has(file.path)) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setContextMenu({mouseX: e.clientX, mouseY: e.clientY, filePath: file.path});
                                }
                            }}
                            sx={{
                                display: "flex", alignItems: "center", gap: 0.5,
                                pl: (depth + 1) * 1.25 + 0.5, pr: 0.5, py: 0.2,
                                cursor: "pointer", userSelect: "none",
                                borderRadius: 0.5,
                                bgcolor: activeFile === file.path ? "action.selected" : "transparent",
                                borderLeft: activeFile === file.path ? "2px solid" : "2px solid transparent",
                                borderColor: activeFile === file.path ? "primary.main" : "transparent",
                                "&:hover": {bgcolor: "action.hover"},
                            }}
                        >
                            {fileIcon(file.name, file.readOnly, activeFile === file.path)}
                            <Typography variant="caption" noWrap sx={{
                                fontSize: 11, fontFamily: "monospace",
                                flex: 1, minWidth: 0,
                                color: file.readOnly ? "text.disabled" : activeFile === file.path ? "text.primary" : "text.secondary",
                                fontStyle: file.readOnly ? "italic" : "normal",
                            }}>
                                {file.name}
                            </Typography>
                            {onRefreshFile && (file.readOnly || refreshableFiles.has(file.name)) && (
                                <Tooltip title={file.readOnly ? "Resynchroniser depuis le backend" : "Réinitialiser depuis le template"} placement="top" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); onRefreshFile(file.path, file.readOnly); }}
                                        sx={{p: 0.15, color: "text.disabled", "&:hover": {color: "primary.main"}}}
                                    >
                                        <SyncRoundedIcon sx={{fontSize: 13}}/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    ))}
                </>
            )}

            {/* Context menu */}
            <Menu
                open={contextMenu !== null}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? {top: contextMenu.mouseY, left: contextMenu.mouseX} : undefined}
                slotProps={{paper: {sx: {minWidth: 160, borderRadius: 1.5}}}}
            >
                <MenuItem onClick={() => { onCreateFile(folderPath); setContextMenu(null); }} sx={{fontSize: 12, gap: 1, py: 0.5}}>
                    <ListItemIcon sx={{minWidth: 0}}><NoteAddRoundedIcon sx={{fontSize: 16, color: "grey.400"}}/></ListItemIcon>
                    <ListItemText primaryTypographyProps={{fontSize: 12}}>Nouveau fichier</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { onCreateFolder(folderPath); setContextMenu(null); }} sx={{fontSize: 12, gap: 1, py: 0.5}}>
                    <ListItemIcon sx={{minWidth: 0}}><CreateNewFolderRoundedIcon sx={{fontSize: 16, color: "grey.400"}}/></ListItemIcon>
                    <ListItemText primaryTypographyProps={{fontSize: 12}}>Nouveau dossier</ListItemText>
                </MenuItem>
                {contextMenu?.filePath && !protectedFiles.has(contextMenu.filePath) && (
                    <MenuItem onClick={() => { onDeleteFile(contextMenu.filePath); setContextMenu(null); }} sx={{fontSize: 12, gap: 1, py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 0}}><DeleteRoundedIcon sx={{fontSize: 16, color: "error.main"}}/></ListItemIcon>
                        <ListItemText primaryTypographyProps={{fontSize: 12, color: "error.main"}}>Supprimer</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

// ── Composant principal ────────────────────────────────────────────────────
export default function CodeEditorIDE({files, setFiles, language, readOnlyFiles = {}, onRefreshFile}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const [activeFile, setActiveFile] = React.useState(Object.keys(files)[0] || "handler.py");
    const [lintErrors, setLintErrors] = React.useState([]);
    const [linting, setLinting] = React.useState(false);
    const editorRef = React.useRef(null);
    const monacoRef = React.useRef(null);
    const decorationsRef = React.useRef([]);

    // Inline creation state
    const [creating, setCreating] = React.useState(null); // {type: "file"|"folder", parentPath: string}
    const [newName, setNewName] = React.useState("");

    const fileNames = Object.keys(files);
    const readOnlyFileNames = Object.keys(readOnlyFiles);
    const isReadOnly = readOnlyFileNames.includes(activeFile);
    const content = isReadOnly ? (readOnlyFiles[activeFile] ?? "") : (files[activeFile] ?? "");

    const ext = language === "R" ? ".R" : ".py";
    const protectedFiles = React.useMemo(() => new Set([`handler${ext}`, "requirements.txt"]), [ext]);
    // Fichiers utilisateur réinitialisables depuis le template (bouton refresh dans l'arbre)
    const refreshableFiles = React.useMemo(() => new Set(["requirements.txt", "install_packages.R"]), []);

    // Arbre de fichiers
    const tree = React.useMemo(() => buildTree(fileNames, readOnlyFileNames), [fileNames, readOnlyFileNames]);

    // Précharger Pyodide en arrière-plan
    React.useEffect(() => { preloadPyodide(); }, []);

    // Lint debounced (1s)
    React.useEffect(() => {
        if (language !== "PYTHON" || !activeFile.endsWith(".py")) {
            setLintErrors([]);
            return;
        }
        setLinting(true);
        const timer = setTimeout(async () => {
            const errs = await lintPython(content);
            setLintErrors(errs);
            setLinting(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [content, language, activeFile]);

    // Appliquer les markers Monaco depuis les erreurs de lint
    React.useEffect(() => {
        const monaco = monacoRef.current;
        const editor = editorRef.current;
        if (!monaco || !editor) return;
        const model = editor.getModel();
        if (!model) return;

        const markers = lintErrors.map(err => ({
            severity: err.severity === "error"
                ? monaco.MarkerSeverity.Error
                : monaco.MarkerSeverity.Warning,
            startLineNumber: err.line,
            startColumn: err.col || 1,
            endLineNumber: err.line,
            endColumn: model.getLineMaxColumn(err.line),
            message: err.msg,
            source: "pyodide-linter",
        }));
        monaco.editor.setModelMarkers(model, "pyodide-linter", markers);
    }, [lintErrors]);

    // Appliquer les décorations de zones verrouillées
    const applyLockDecorations = React.useCallback((editor, monaco, text) => {
        const lockRanges = computeLockRanges(text);
        const newDecorations = lockRanges.map(r => ({
            range: new monaco.Range(r.start + 1, 1, r.end + 1, 1),
            options: {
                isWholeLine: true,
                className: "locked-zone-line",
                glyphMarginClassName: "locked-zone-glyph",
                linesDecorationsClassName: "locked-zone-border",
            },
        }));
        decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecorations);
    }, []);

    // Protéger les zones LOCK contre l'édition
    const setupLockProtection = React.useCallback((editor, monaco) => {
        editor.onDidChangeModelContent((e) => {
            // Vérifié après chaque frappe : le contenu actuel a-t-il encore toutes ses zones LOCK intactes ?
        });
    }, []);

    function handleEditorDidMount(editor, monaco) {
        editorRef.current = editor;
        monacoRef.current = monaco;

        // Définir les thèmes custom (clair + sombre)
        monaco.editor.defineTheme("promise-dark", {
            base: "vs-dark",
            inherit: true,
            rules: [],
            colors: {
                "editor.background": "#1e1e1e",
                "editorGutter.background": "#252526",
            },
        });
        monaco.editor.defineTheme("promise-light", {
            base: "vs",
            inherit: true,
            rules: [],
            colors: {
                "editor.background": "#ffffff",
                "editorGutter.background": "#f5f5f5",
            },
        });
        monaco.editor.setTheme(isDark ? "promise-dark" : "promise-light");

        // Ajouter CSS pour les zones verrouillées
        const style = document.getElementById("monaco-lock-styles") || document.createElement("style");
        style.id = "monaco-lock-styles";
        style.textContent = `
            .locked-zone-line { background-color: rgba(229, 57, 53, 0.08) !important; }
            .locked-zone-border { background-color: #e53935; width: 3px !important; margin-left: 3px; }
            .locked-zone-glyph { }
        `;
        if (!document.getElementById("monaco-lock-styles")) document.head.appendChild(style);

        applyLockDecorations(editor, monaco, content);
        setupLockProtection(editor, monaco);

        // Enregistrer la complétion pour Python
        if (!monaco.__promiseCompletionRegistered) {
            monaco.__promiseCompletionRegistered = true;
            registerPythonCompletion(monaco);
            registerRCompletion(monaco);
        }
    }

    function handleEditorChange(value) {
        if (isReadOnly || value === undefined || value === null) return;

        // Vérifier que les zones LOCK n'ont pas été modifiées
        const oldLocks = computeLockRanges(content);
        const oldLines = content.split("\n");

        for (const r of oldLocks) {
            const oldLockContent = oldLines.slice(r.start, r.end + 1).join("\n");
            if (!value.includes(oldLockContent)) {
                const editor = editorRef.current;
                if (editor) {
                    // Différer le setValue hors du cycle onChange pour éviter
                    // le crash "_acceptDeleteRange" dans le worker Monaco
                    setTimeout(() => {
                        if (editorRef.current) {
                            editorRef.current.setValue(content);
                            applyLockDecorations(editorRef.current, monacoRef.current, content);
                        }
                    }, 0);
                }
                return;
            }
        }

        setFiles(prev => ({...prev, [activeFile]: value}));

        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (editor && monaco) {
            applyLockDecorations(editor, monaco, value);
        }
    }

    // Suivre le changement de thème
    React.useEffect(() => {
        const monaco = monacoRef.current;
        if (monaco) monaco.editor.setTheme(isDark ? "promise-dark" : "promise-light");
    }, [isDark]);

    // Quand on change de fichier actif, réappliquer les décorations
    React.useEffect(() => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (editor && monaco) {
            setTimeout(() => applyLockDecorations(editor, monaco, content), 50);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFile]);

    // ── Actions fichiers / dossiers ───────────────────────────────────────
    function handleCreateFile(parentPath) {
        setCreating({type: "file", parentPath});
        setNewName("");
    }

    function handleCreateFolder(parentPath) {
        setCreating({type: "folder", parentPath});
        setNewName("");
    }

    function confirmCreate() {
        if (!newName.trim()) { setCreating(null); return; }
        const name = newName.trim();
        const parentPath = creating.parentPath;
        const fullPath = parentPath ? `${parentPath}/${name}` : name;

        if (creating.type === "file") {
            setFiles(prev => ({...prev, [fullPath]: ""}));
            setActiveFile(fullPath);
        } else {
            // Créer un dossier = créer un fichier placeholder .gitkeep dedans
            // On ne crée pas réellement de dossier vide, on attend que l'utilisateur y crée un fichier
            // Mais pour que le dossier apparaisse, on crée un fichier vide temporaire
            const placeholderPath = `${fullPath}/.gitkeep`;
            setFiles(prev => ({...prev, [placeholderPath]: ""}));
        }
        setCreating(null);
        setNewName("");
    }

    function handleDeleteFile(path) {
        if (protectedFiles.has(path)) return;
        setFiles(prev => {
            const next = {...prev};
            delete next[path];
            return next;
        });
        if (activeFile === path) {
            const remaining = fileNames.filter(f => f !== path);
            setActiveFile(remaining[0] || "handler.py");
        }
    }

    const errCount = lintErrors.filter(e => e.severity === "error").length;
    const warnCount = lintErrors.filter(e => e.severity === "warning").length;
    const lockRanges = computeLockRanges(content);

    // Déterminer le langage Monaco
    function monacoLanguage() {
        if (activeFile.endsWith(".py")) return "python";
        if (activeFile.endsWith(".R")) return "r";
        if (activeFile.endsWith(".json")) return "json";
        if (activeFile.endsWith(".yaml") || activeFile.endsWith(".yml")) return "yaml";
        if (activeFile.endsWith(".txt")) return "plaintext";
        return "python";
    }

    return (
        <Paper variant="outlined" sx={{borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "row", flex: 1, minHeight: 300}}>
            {/* ── Panneau gauche : explorateur de fichiers ── */}
            <Box sx={{
                width: 200, minWidth: 160, maxWidth: 260,
                bgcolor: isDark ? "grey.900" : "grey.50",
                borderRight: "1px solid", borderColor: "divider",
                display: "flex", flexDirection: "column", flexShrink: 0,
                overflow: "hidden",
            }}>
                {/* Header explorateur */}
                <Stack direction="row" sx={{
                    px: 1, py: 0.5, alignItems: "center", justifyContent: "space-between",
                    borderBottom: "1px solid", borderColor: "divider",
                }}>
                    <Typography variant="caption" sx={{fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5}}>
                        Fichiers
                    </Typography>
                    <Stack direction="row" sx={{gap: 0}}>
                        <Tooltip title="Nouveau fichier" placement="top">
                            <IconButton size="small" onClick={() => handleCreateFile("")} sx={{color: "text.secondary", p: 0.25}}>
                                <NoteAddRoundedIcon sx={{fontSize: 14}}/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Nouveau dossier" placement="top">
                            <IconButton size="small" onClick={() => handleCreateFolder("")} sx={{color: "text.secondary", p: 0.25}}>
                                <CreateNewFolderRoundedIcon sx={{fontSize: 14}}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Arbre de fichiers */}
                <Box sx={{flex: 1, overflowY: "auto", overflowX: "hidden", py: 0.5, px: 0.25}}>
                    <TreeNode
                        node={tree}
                        depth={0}
                        activeFile={activeFile}
                        onSelectFile={setActiveFile}
                        onCreateFile={handleCreateFile}
                        onCreateFolder={handleCreateFolder}
                        onDeleteFile={handleDeleteFile}
                        onRefreshFile={onRefreshFile}
                        refreshableFiles={refreshableFiles}
                        protectedFiles={protectedFiles}
                    />
                </Box>

                {/* Zone de création inline */}
                {creating && (
                    <Box sx={{px: 0.5, py: 0.5, borderTop: "1px solid", borderColor: "divider"}}>
                        <Stack direction="row" sx={{alignItems: "center", gap: 0.25}}>
                            {creating.type === "folder"
                                ? <CreateNewFolderRoundedIcon sx={{fontSize: 14, color: "#e8a838"}}/>
                                : <NoteAddRoundedIcon sx={{fontSize: 14, color: "grey.400"}}/>}
                            <TextField
                                size="small" autoFocus fullWidth
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") confirmCreate();
                                    if (e.key === "Escape") setCreating(null);
                                }}
                                onBlur={() => { if (newName.trim()) confirmCreate(); else setCreating(null); }}
                                placeholder={creating.type === "folder" ? "dossier" : "fichier.py"}
                                sx={{
                                    "& input": {fontSize: 11, py: 0.15, px: 0.5, fontFamily: "monospace"},
                                    "& fieldset": {borderColor: "divider"},
                                }}
                            />
                        </Stack>
                        {creating.parentPath && (
                            <Typography variant="caption" sx={{fontSize: 9, color: "grey.600", pl: 2.5, display: "block"}}>
                                dans {creating.parentPath}/
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            {/* ── Panneau droit : éditeur ── */}
            <Box sx={{flex: 1, display: "flex", flexDirection: "column", minWidth: 0}}>
                {/* Monaco Editor */}
                <Box sx={{flex: 1, minHeight: 300}}>
                    <Editor
                        key={activeFile}
                        height="100%"
                        language={monacoLanguage()}
                        value={content}
                        onChange={handleEditorChange}
                        onMount={handleEditorDidMount}
                        theme={isDark ? "promise-dark" : "promise-light"}
                        path={activeFile}
                        options={{
                            readOnly: isReadOnly,
                            minimap: {enabled: false},
                            fontSize: 13,
                            fontFamily: "'Fira Code', Consolas, Monaco, monospace",
                            fontLigatures: true,
                            lineNumbers: "on",
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            tabSize: 4,
                            insertSpaces: true,
                            wordWrap: "off",
                            renderWhitespace: "selection",
                            bracketPairColorization: {enabled: true},
                            guides: {bracketPairs: true, indentation: true},
                            suggest: {
                                showKeywords: true,
                                showSnippets: true,
                                showFunctions: true,
                                showVariables: true,
                            },
                            quickSuggestions: {other: true, comments: false, strings: false},
                            parameterHints: {enabled: true},
                            folding: true,
                            glyphMargin: true,
                            scrollbar: {verticalScrollbarSize: 8, horizontalScrollbarSize: 8},
                        }}
                    />
                </Box>

                {/* Status bar */}
                <Stack direction="row" sx={{
                    bgcolor: isDark ? "grey.900" : "grey.100",
                    px: 1.5, py: 0.25, gap: 1.5,
                    borderTop: "1px solid", borderColor: "divider",
                    alignItems: "center", flexShrink: 0,
                }}>
                    <Chip label={language} size="small" sx={{height: 16, fontSize: 9, bgcolor: language === "PYTHON" ? "#306998" : "#276DC3", color: "#fff"}}/>
                    <Chip label={`${content.split("\n").length} lignes`} size="small" sx={{height: 16, fontSize: 9, bgcolor: isDark ? "grey.800" : "grey.300", color: "text.secondary"}}/>
                    <Chip label={`${lockRanges.length} lock`} size="small"
                          sx={{height: 16, fontSize: 9, bgcolor: lockRanges.length > 0 ? "error.dark" : isDark ? "grey.800" : "grey.300", color: lockRanges.length > 0 ? "#fff" : "text.secondary"}}/>
                    <Chip label={activeFile} size="small" sx={{height: 16, fontSize: 9, bgcolor: isDark ? "grey.800" : "grey.300", color: "text.secondary", fontFamily: "monospace"}}/>
                    {isPyodideReady() && <Chip label="Pyodide" size="small" sx={{height: 16, fontSize: 9, bgcolor: "#306998", color: "#fff"}}/>}
                    {linting && <Chip label="analyse..." size="small" sx={{height: 16, fontSize: 9, bgcolor: isDark ? "grey.800" : "grey.300", color: "text.secondary"}}/>}

                    <Box sx={{flex: 1}}/>

                    {errCount > 0 && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.25}}>
                            <ErrorOutlineIcon sx={{fontSize: 11, color: "#ff5252"}}/>
                            <Typography variant="caption" sx={{fontSize: 10, color: "#ff5252", fontWeight: 700}}>{errCount}</Typography>
                        </Box>
                    )}
                    {warnCount > 0 && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.25}}>
                            <WarningAmberIcon sx={{fontSize: 11, color: "#ffb74d"}}/>
                            <Typography variant="caption" sx={{fontSize: 10, color: "#ffb74d", fontWeight: 700}}>{warnCount}</Typography>
                        </Box>
                    )}
                    {!linting && errCount === 0 && warnCount === 0 && language === "PYTHON" && activeFile.endsWith(".py") && (
                        <Chip label="OK" size="small" sx={{height: 16, fontSize: 9, bgcolor: "#1b5e20", color: "#fff"}}/>
                    )}
                </Stack>
            </Box>
        </Paper>
    );
}

// ── Autocomplétion Python ──────────────────────────────────────────────────
function registerPythonCompletion(monaco) {
    monaco.languages.registerCompletionItemProvider("python", {
        triggerCharacters: [".", " "],
        provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };

            // Ligne courante pour contexte
            const lineContent = model.getLineContent(position.lineNumber);
            const textBefore = lineContent.substring(0, position.column - 1);

            const suggestions = [];

            // Complétion après "np."
            if (textBefore.endsWith("np.")) {
                const npItems = [
                    "array", "zeros", "ones", "empty", "arange", "linspace", "logspace",
                    "mean", "std", "var", "median", "sum", "min", "max", "argmin", "argmax",
                    "abs", "sqrt", "exp", "log", "log2", "log10", "power", "square",
                    "dot", "matmul", "transpose", "reshape", "flatten", "ravel",
                    "concatenate", "stack", "vstack", "hstack", "split",
                    "where", "nonzero", "sort", "argsort", "unique",
                    "random", "nan", "inf", "pi", "isnan", "isinf", "isfinite",
                    "float64", "int64", "bool_", "ndarray",
                    "polyfit", "polyval", "corrcoef", "cov",
                    "fft", "linalg", "clip", "round", "ceil", "floor",
                ];
                npItems.forEach(item => suggestions.push({
                    label: item, kind: monaco.languages.CompletionItemKind.Function,
                    insertText: item, range, detail: "numpy",
                }));
                return {suggestions};
            }

            // Complétion après "pd."
            if (textBefore.endsWith("pd.")) {
                const pdItems = [
                    "DataFrame", "Series", "read_csv", "read_json", "read_excel",
                    "concat", "merge", "pivot_table", "crosstab", "cut", "qcut",
                    "to_datetime", "to_numeric", "isna", "notna", "NA", "NaT",
                ];
                pdItems.forEach(item => suggestions.push({
                    label: item, kind: monaco.languages.CompletionItemKind.Function,
                    insertText: item, range, detail: "pandas",
                }));
                return {suggestions};
            }

            // Complétion après "params."
            if (textBefore.endsWith("params.")) {
                ["get", "keys", "values", "items", "pop", "setdefault", "update"].forEach(item =>
                    suggestions.push({
                        label: item, kind: monaco.languages.CompletionItemKind.Method,
                        insertText: item === "get" ? 'get("${1:key}", ${2:None})' : item,
                        insertTextRules: item === "get" ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
                        range, detail: "dict",
                    })
                );
                return {suggestions};
            }

            // Snippets et mots-clés Python généraux
            const pyKeywords = [
                {label: "def", insertText: "def ${1:name}(${2:args}):\n    ${0}", detail: "function"},
                {label: "for", insertText: "for ${1:item} in ${2:iterable}:\n    ${0}", detail: "loop"},
                {label: "if", insertText: "if ${1:condition}:\n    ${0}", detail: "condition"},
                {label: "elif", insertText: "elif ${1:condition}:\n    ${0}", detail: "condition"},
                {label: "else", insertText: "else:\n    ${0}", detail: "condition"},
                {label: "while", insertText: "while ${1:condition}:\n    ${0}", detail: "loop"},
                {label: "try", insertText: "try:\n    ${1}\nexcept ${2:Exception} as e:\n    ${0}", detail: "exception"},
                {label: "with", insertText: "with ${1:expr} as ${2:var}:\n    ${0}", detail: "context"},
                {label: "class", insertText: "class ${1:Name}:\n    def __init__(self${2:, args}):\n        ${0}", detail: "class"},
                {label: "lambda", insertText: "lambda ${1:x}: ${0}", detail: "lambda"},
                {label: "return", insertText: "return ${0}", detail: "keyword"},
                {label: "import", insertText: "import ${0}", detail: "import"},
                {label: "from", insertText: "from ${1:module} import ${0}", detail: "import"},
                {label: "print", insertText: 'print(${0})', detail: "builtin"},
                {label: "len", insertText: "len(${0})", detail: "builtin"},
                {label: "range", insertText: "range(${0})", detail: "builtin"},
                {label: "enumerate", insertText: "enumerate(${0})", detail: "builtin"},
                {label: "zip", insertText: "zip(${0})", detail: "builtin"},
                {label: "isinstance", insertText: "isinstance(${1:obj}, ${2:type})", detail: "builtin"},
                {label: "list", insertText: "list(${0})", detail: "builtin"},
                {label: "dict", insertText: "dict(${0})", detail: "builtin"},
                {label: "set", insertText: "set(${0})", detail: "builtin"},
                {label: "tuple", insertText: "tuple(${0})", detail: "builtin"},
                {label: "sorted", insertText: "sorted(${0})", detail: "builtin"},
                {label: "map", insertText: "map(${1:func}, ${2:iterable})", detail: "builtin"},
                {label: "filter", insertText: "filter(${1:func}, ${2:iterable})", detail: "builtin"},
            ];
            pyKeywords.forEach(kw => suggestions.push({
                label: kw.label,
                kind: kw.detail === "keyword" || kw.detail === "condition" || kw.detail === "loop"
                    ? monaco.languages.CompletionItemKind.Keyword
                    : monaco.languages.CompletionItemKind.Snippet,
                insertText: kw.insertText,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range, detail: kw.detail,
            }));

            // Complétion sklearn courante
            const sklearnImports = [
                {label: "KMeans", text: "from sklearn.cluster import KMeans", detail: "sklearn.cluster"},
                {label: "DBSCAN", text: "from sklearn.cluster import DBSCAN", detail: "sklearn.cluster"},
                {label: "silhouette_score", text: "from sklearn.metrics import silhouette_score", detail: "sklearn.metrics"},
                {label: "StandardScaler", text: "from sklearn.preprocessing import StandardScaler", detail: "sklearn.preprocessing"},
                {label: "MinMaxScaler", text: "from sklearn.preprocessing import MinMaxScaler", detail: "sklearn.preprocessing"},
                {label: "LinearRegression", text: "from sklearn.linear_model import LinearRegression", detail: "sklearn.linear_model"},
                {label: "train_test_split", text: "from sklearn.model_selection import train_test_split", detail: "sklearn.model_selection"},
            ];
            sklearnImports.forEach(s => suggestions.push({
                label: s.label, kind: monaco.languages.CompletionItemKind.Module,
                insertText: s.text, range, detail: s.detail,
            }));

            return {suggestions};
        },
    });
}

// ── Autocomplétion R ───────────────────────────────────────────────────────
function registerRCompletion(monaco) {
    monaco.languages.registerCompletionItemProvider("r", {
        triggerCharacters: ["(", ",", " "],
        provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };

            const suggestions = [];
            const rItems = [
                {label: "function", insertText: "function(${1:args}) {\n    ${0}\n}", detail: "keyword"},
                {label: "if", insertText: "if (${1:condition}) {\n    ${0}\n}", detail: "keyword"},
                {label: "else", insertText: "else {\n    ${0}\n}", detail: "keyword"},
                {label: "for", insertText: "for (${1:i} in ${2:seq}) {\n    ${0}\n}", detail: "keyword"},
                {label: "while", insertText: "while (${1:condition}) {\n    ${0}\n}", detail: "keyword"},
                {label: "tryCatch", insertText: "tryCatch({\n    ${1}\n}, error = function(e) {\n    ${0}\n})", detail: "error handling"},
                {label: "library", insertText: "library(${0})", detail: "import"},
                {label: "list", insertText: "list(${0})", detail: "data structure"},
                {label: "c", insertText: "c(${0})", detail: "combine"},
                {label: "data.frame", insertText: "data.frame(${0})", detail: "data structure"},
                {label: "lapply", insertText: "lapply(${1:X}, ${2:FUN})", detail: "apply"},
                {label: "sapply", insertText: "sapply(${1:X}, ${2:FUN})", detail: "apply"},
                {label: "mean", insertText: "mean(${0})", detail: "stats"},
                {label: "sd", insertText: "sd(${0})", detail: "stats"},
                {label: "var", insertText: "var(${0})", detail: "stats"},
                {label: "median", insertText: "median(${0})", detail: "stats"},
                {label: "sum", insertText: "sum(${0})", detail: "stats"},
                {label: "length", insertText: "length(${0})", detail: "base"},
                {label: "nrow", insertText: "nrow(${0})", detail: "base"},
                {label: "ncol", insertText: "ncol(${0})", detail: "base"},
                {label: "paste", insertText: "paste(${0})", detail: "string"},
                {label: "paste0", insertText: "paste0(${0})", detail: "string"},
                {label: "sprintf", insertText: 'sprintf("${1:%s}", ${0})', detail: "string"},
                {label: "is.null", insertText: "is.null(${0})", detail: "test"},
                {label: "is.na", insertText: "is.na(${0})", detail: "test"},
                {label: "as.integer", insertText: "as.integer(${0})", detail: "cast"},
                {label: "as.numeric", insertText: "as.numeric(${0})", detail: "cast"},
                {label: "as.character", insertText: "as.character(${0})", detail: "cast"},
                {label: "as.logical", insertText: "as.logical(${0})", detail: "cast"},
            ];
            rItems.forEach(item => suggestions.push({
                label: item.label,
                kind: item.detail === "keyword"
                    ? monaco.languages.CompletionItemKind.Keyword
                    : monaco.languages.CompletionItemKind.Function,
                insertText: item.insertText,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range, detail: item.detail,
            }));
            return {suggestions};
        },
    });
}
